"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "./ui/button";
import { Search, Plus, X } from "lucide-react";
import { getWalletAddresses } from "../services/digitalWalletService";
import { useToast } from "../hooks/use-toast";
import selectButton from "../assets/icons/select_button.png";

const ContactsPopup = ({
  isOpen,
  onClose,
  onSelectContact,
  onAddNewContact,
  savedAddresses = [],
}) => {
  const [savedWallets, setSavedWallets] = useState([]);
  const [filteredWallets, setFilteredWallets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { toast } = useToast();
  const isFetching = useRef(false);
  const hasFetched = useRef(false);

  const removeDuplicateWallets = (wallets) => {
    const uniqueWallets = [];
    const seenIds = new Set();

    for (const wallet of wallets) {
      if (wallet._id && !seenIds.has(wallet._id)) {
        seenIds.add(wallet._id);
        uniqueWallets.push(wallet);
      }
    }

    return uniqueWallets;
  };

  const fetchSavedWallets = useCallback(async () => {
    if (isFetching.current) return;

    isFetching.current = true;
    try {
      setLoading(true);
      setError(null);
      const response = await getWalletAddresses();
      let wallets = [];

      if (response?.data?.data?.address) {
        wallets = response.data.data.address;
      }

      if (savedAddresses && savedAddresses.length > 0) {
        wallets = [...wallets, ...savedAddresses];
      }

      const uniqueWallets = removeDuplicateWallets(wallets);

      setSavedWallets(uniqueWallets);
      setFilteredWallets(uniqueWallets);
      hasFetched.current = true;
    } catch (err) {
      setError("Failed to load saved wallets");
      toast({
        description: "Failed to load saved wallets",
        variant: "eighty",
      });
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [savedAddresses, toast]);

  useEffect(() => {
    if (isOpen && !hasFetched.current && !isFetching.current) {
      fetchSavedWallets();
    }
  }, [isOpen, fetchSavedWallets]);

  useEffect(() => {
    if (!isOpen) {
      hasFetched.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      savedAddresses &&
      savedAddresses.length > 0 &&
      savedWallets.length > 0
    ) {
      const uniqueWallets = removeDuplicateWallets([
        ...savedWallets,
        ...savedAddresses,
      ]);
      setSavedWallets(uniqueWallets);
      setFilteredWallets(uniqueWallets);
    }
  }, [savedAddresses, isOpen, savedWallets]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredWallets(savedWallets);
      return;
    }

    const filtered = savedWallets.filter(
      (wallet) =>
        wallet.firstName?.toLowerCase().includes(value.toLowerCase()) ||
        wallet.lastName?.toLowerCase().includes(value.toLowerCase()) ||
        wallet.addressName?.toLowerCase().includes(value.toLowerCase()) ||
        wallet.walletAddress?.toLowerCase().includes(value.toLowerCase()) ||
        wallet.assetName?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredWallets(filtered);
  };

  const handleSelectWallet = (wallet) => {
    onSelectContact(wallet);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="pt-4 pr-4 flex justify-end">
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors p-2"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
          </button>
        </div>
        <div className="flex items-center justify-between px-6 pb-4 border-b border-[#EBE9EF]">
          <h2 className="text-[20px] font-semibold text-[#6D6584]">
            Address book
          </h2>
          <div className="py-4 pl-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#F5F3F8] rounded-full outline-none focus:ring-2 focus:ring-[#57249C] text-[12px]"
              />
            </div>
          </div>
        </div>

        <div className="py-4 px-6">
          <Button
            onClick={() => {
              onAddNewContact();
              onClose();
            }}
            className="bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white rounded-full font-semibold flex-1 h-[48px] !w-[259px] flex items-center justify-center gap-2 ml-auto"
          >
            <Plus className="w-4 h-4" />
            Add new contact
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto mb-6 min-h-[40vh]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Loading wallets...</p>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-rose-500">{error}</p>
            </div>
          ) : filteredWallets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12">
              <p className="text-gray-500 text-center mb-4">
                {searchTerm ? "No wallets found" : "No saved wallets yet"}
              </p>
            </div>
          ) : (
            <div className="mx-6">
              {filteredWallets.map((wallet) => (
                <div
                  key={wallet._id}
                  onClick={() => handleSelectWallet(wallet)}
                  className="px-4 h-[88px] hover:bg-[#F5F3F8] border cursor-pointer transition-colors flex items-center justify-between rounded-2xl mb-2"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-[40px] h-[40px] rounded-full bg-[#33125F] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                      {(wallet.firstName?.charAt(0).toUpperCase() || "") +
                        (wallet.lastName?.charAt(0).toUpperCase() || "")}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-[20px] text-[#423E51] truncate">
                          {wallet.firstName} {wallet.lastName}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="ml-4 text-gray-300 flex items-center gap-6">
                    {wallet.assetIcon && (
                      <img
                        src={wallet.assetIcon}
                        alt={wallet.assetName}
                        className="w-[24px] h-[24px] rounded-full"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <img
                      src={selectButton}
                      className="w-[40px] h-[40px]"
                      alt="Select Button"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactsPopup;
